import { useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EMPTY_CONTACT = { first_name: "", last_name: "", phone: "", email: "" };
const EMPTY_INS = { provider: "", member_id: "", group_number: "", plan: "" };

export function VaccineDriveForm({ initial, onSubmit, submitLabel = "Save changes", onCancel, showStatus = false }) {
  const [d, setD] = useState(initial);
  const setF = (k, v) => setD((x) => ({ ...x, [k]: v }));
  const setContact = (i, k, v) => setD((x) => ({ ...x, contacts: x.contacts.map((c, idx) => (idx === i ? { ...c, [k]: v } : c)) }));
  const setPatient = (i, k, v) => setD((x) => ({ ...x, patients: x.patients.map((p, idx) => (idx === i ? { ...p, [k]: v } : p)) }));
  const toggleVaccine = (pi, key) => setD((x) => ({ ...x, patients: x.patients.map((p, idx) => {
    if (idx !== pi) return p;
    const cur = p.vaccines && p.vaccines.length ? p.vaccines : (p.vaccine ? [p.vaccine] : []);
    const next = cur.includes(key) ? cur.filter((v) => v !== key) : [...cur, key];
    return { ...p, vaccines: next };
  }) }));
  const setIns = (pi, ii, k, v) => setD((x) => ({ ...x, patients: x.patients.map((p, idx) => idx === pi ? { ...p, insurances: p.insurances.map((ins, j) => (j === ii ? { ...ins, [k]: v } : ins)) } : p) }));

  function submit(e) {
    e.preventDefault();
    if (!d.address || !d.date || !d.time) return toast.error("Address, date and time are required.");
    if (d.contacts.some((c) => !c.first_name || !c.last_name || !c.email)) return toast.error("Each contact needs first name, last name and email.");
    const n = (v) => Number(v) || 0;
    const patients = (d.patients || []).map((p) => {
      const vaccines = p.vaccines && p.vaccines.length ? p.vaccines : (p.vaccine ? [p.vaccine] : []);
      return { ...p, vaccines, vaccine: vaccines[0] || "" };
    });
    onSubmit({ ...d, patients, staff_count: n(d.staff_count), residents_count: n(d.residents_count),
      staff_flu: n(d.staff_flu), staff_covid: n(d.staff_covid), staff_rsv: n(d.staff_rsv),
      residents_flu: n(d.residents_flu), residents_covid: n(d.residents_covid), residents_rsv: n(d.residents_rsv) });
  }

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="vaccine-drive-form">
      <div className="space-y-2"><Label>Address</Label><Input data-testid="vdf-address" value={d.address} onChange={(e) => setF("address", e.target.value)} required /></div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2"><Label>Date</Label><Input type="date" data-testid="vdf-date" value={d.date} onChange={(e) => setF("date", e.target.value)} required /></div>
        <div className="space-y-2"><Label>Time</Label><Input type="time" data-testid="vdf-time" value={d.time} onChange={(e) => setF("time", e.target.value)} required /></div>
      </div>
      {showStatus && (
        <div className="space-y-2">
          <Label>Status</Label>
          <Select value={d.status || "pending"} onValueChange={(v) => setF("status", v)}>
            <SelectTrigger data-testid="vdf-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="confirmed">Confirmed</SelectItem>
              <SelectItem value="rescheduled">Rescheduled</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label>Contact person(s)</Label>
          <Button type="button" size="sm" variant="outline" className="rounded-full" onClick={() => setF("contacts", [...d.contacts, { ...EMPTY_CONTACT }])} data-testid="vdf-add-contact"><Plus className="mr-1 h-4 w-4" /> Add a contact person</Button>
        </div>
        {d.contacts.map((c, i) => (
          <div key={i} className="rounded-xl border border-border/60 p-3">
            <div className="mb-2 flex items-center justify-between"><span className="text-xs font-semibold text-muted-foreground">Contact {i + 1}</span>{d.contacts.length > 1 && <button type="button" onClick={() => setF("contacts", d.contacts.filter((_, x) => x !== i))} className="text-destructive"><Trash2 className="h-4 w-4" /></button>}</div>
            <div className="grid gap-3 sm:grid-cols-2">
              <Input placeholder="First name" value={c.first_name} onChange={(e) => setContact(i, "first_name", e.target.value)} required />
              <Input placeholder="Last name" value={c.last_name} onChange={(e) => setContact(i, "last_name", e.target.value)} required />
              <Input placeholder="Phone" value={c.phone} onChange={(e) => setContact(i, "phone", e.target.value)} />
              <Input placeholder="Email" type="email" value={c.email} onChange={(e) => setContact(i, "email", e.target.value)} required />
            </div>
          </div>
        ))}
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-3 rounded-xl border border-border/60 p-4">
          <Label className="text-sm font-semibold">Staff</Label>
          <div className="space-y-1.5"><Label className="text-xs">How many requested a vaccination?</Label><Input type="number" min="0" data-testid="vdf-staff" value={d.staff_count} onChange={(e) => setF("staff_count", e.target.value)} /></div>
          <div className="grid grid-cols-3 gap-2">
            <div className="space-y-1"><Label className="text-xs">Flu</Label><Input type="number" min="0" data-testid="vdf-staff-flu" value={d.staff_flu} onChange={(e) => setF("staff_flu", e.target.value)} /></div>
            <div className="space-y-1"><Label className="text-xs">COVID</Label><Input type="number" min="0" data-testid="vdf-staff-covid" value={d.staff_covid} onChange={(e) => setF("staff_covid", e.target.value)} /></div>
            <div className="space-y-1"><Label className="text-xs">RSV</Label><Input type="number" min="0" data-testid="vdf-staff-rsv" value={d.staff_rsv} onChange={(e) => setF("staff_rsv", e.target.value)} /></div>
          </div>
        </div>
        <div className="space-y-3 rounded-xl border border-border/60 p-4">
          <Label className="text-sm font-semibold">Residents</Label>
          <div className="space-y-1.5"><Label className="text-xs">How many requested a vaccination?</Label><Input type="number" min="0" data-testid="vdf-residents" value={d.residents_count} onChange={(e) => setF("residents_count", e.target.value)} /></div>
          <div className="grid grid-cols-3 gap-2">
            <div className="space-y-1"><Label className="text-xs">Flu</Label><Input type="number" min="0" data-testid="vdf-residents-flu" value={d.residents_flu} onChange={(e) => setF("residents_flu", e.target.value)} /></div>
            <div className="space-y-1"><Label className="text-xs">COVID</Label><Input type="number" min="0" data-testid="vdf-residents-covid" value={d.residents_covid} onChange={(e) => setF("residents_covid", e.target.value)} /></div>
            <div className="space-y-1"><Label className="text-xs">RSV</Label><Input type="number" min="0" data-testid="vdf-residents-rsv" value={d.residents_rsv} onChange={(e) => setF("residents_rsv", e.target.value)} /></div>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div><Label>Patient roster</Label><p className="text-xs text-muted-foreground">Full name, date of birth and insurance (optional).</p></div>
          <Button type="button" size="sm" variant="outline" className="rounded-full" onClick={() => setF("patients", [...d.patients, { full_name: "", date_of_birth: "", vaccine: "", vaccines: [], insurance_coverage: "", insurances: [{ ...EMPTY_INS }] }])} data-testid="vdf-add-patient"><Plus className="mr-1 h-4 w-4" /> Add patient</Button>
        </div>
        {d.patients.map((p, pi) => (
          <div key={pi} className="rounded-xl border border-border/60 p-3">
            <div className="mb-2 flex items-center justify-between"><span className="text-xs font-semibold text-muted-foreground">Patient {pi + 1}</span><button type="button" onClick={() => setF("patients", d.patients.filter((_, x) => x !== pi))} className="text-destructive"><Trash2 className="h-4 w-4" /></button></div>
            <div className="grid gap-3 sm:grid-cols-2">
              <Input placeholder="Full name" value={p.full_name} onChange={(e) => setPatient(pi, "full_name", e.target.value)} />
              <Input placeholder="Date of birth" type="date" value={p.date_of_birth} onChange={(e) => setPatient(pi, "date_of_birth", e.target.value)} />
            </div>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <div>
                <Label className="text-xs">Vaccine(s)</Label>
                <div className="mt-1.5 flex flex-wrap gap-4">
                  {[["flu", "Flu"], ["covid", "COVID"], ["rsv", "RSV"]].map(([val, lbl]) => {
                    const sel = (p.vaccines && p.vaccines.length ? p.vaccines : (p.vaccine ? [p.vaccine] : [])).includes(val);
                    return (
                      <label key={val} className="flex cursor-pointer items-center gap-1.5 text-sm">
                        <input type="checkbox" className="h-4 w-4 rounded border-input accent-primary" checked={sel} onChange={() => toggleVaccine(pi, val)} data-testid={`vdf-patient-vaccine-${pi}-${val}`} />
                        {lbl}
                      </label>
                    );
                  })}
                </div>
              </div>
              <div>
                <Label className="text-xs">Insurance coverage</Label>
                <select className="mt-1 h-9 w-full rounded-md border border-input bg-background px-2 text-sm" value={p.insurance_coverage || ""} onChange={(e) => setPatient(pi, "insurance_coverage", e.target.value)} data-testid={`vdf-patient-coverage-${pi}`}>
                  <option value="">Select coverage</option>
                  <option value="covered">Covered by insurance</option>
                  <option value="not_covered">Not covered by insurance</option>
                </select>
              </div>
            </div>
            <div className="mt-3 space-y-2">
              {p.insurances.map((ins, ii) => (
                <div key={ii} className="rounded-lg bg-muted/40 p-2">
                  <div className="mb-1 flex items-center justify-between"><span className="text-[11px] font-medium text-muted-foreground">Insurance {ii + 1}</span>{p.insurances.length > 1 && <button type="button" onClick={() => setPatient(pi, "insurances", p.insurances.filter((_, x) => x !== ii))} className="text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>}</div>
                  <div className="grid gap-2 sm:grid-cols-2">
                    <Input placeholder="Provider" value={ins.provider} onChange={(e) => setIns(pi, ii, "provider", e.target.value)} />
                    <Input placeholder="Member ID" value={ins.member_id} onChange={(e) => setIns(pi, ii, "member_id", e.target.value)} />
                    <Input placeholder="Group #" value={ins.group_number} onChange={(e) => setIns(pi, ii, "group_number", e.target.value)} />
                    <Input placeholder="Plan" value={ins.plan} onChange={(e) => setIns(pi, ii, "plan", e.target.value)} />
                  </div>
                </div>
              ))}
              <Button type="button" size="sm" variant="ghost" className="rounded-full" onClick={() => setPatient(pi, "insurances", [...p.insurances, { ...EMPTY_INS }])}><Plus className="mr-1 h-3.5 w-3.5" /> Add additional insurance</Button>
            </div>
          </div>
        ))}
      </div>

      <div className="flex gap-2">
        <Button type="submit" className="rounded-full" data-testid="vdf-submit">{submitLabel}</Button>
        {onCancel && <Button type="button" variant="ghost" className="rounded-full" onClick={onCancel}>Cancel</Button>}
      </div>
    </form>
  );
}
